import { useState, useEffect } from "react";
import { Menu as MenuIcon, X, ShoppingBag } from "lucide-react";
import { WHATSAPP_LINK, ASSETS } from "../data/menu";
import { useCart } from "../context/CartContext";

const NAV_LINKS = [
  { label: "Inicio", href: "#inicio", testId: "nav-inicio" },
  { label: "Combos", href: "#menu", testId: "nav-menu" },
  { label: "Menú", href: "#menu-general", testId: "nav-menu-general" },
  { label: "Nosotros", href: "#nosotros", testId: "nav-nosotros" },
  { label: "Contacto", href: "#contacto", testId: "nav-contacto" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems, openDrawer } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-[#F4EFE6]/95 backdrop-blur-sm border-b border-[#E6DFD3]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20 sm:h-24">
          <a
            href="#inicio"
            data-testid="brand-logo"
            className="flex items-center gap-3 leading-none"
          >
            <img
              src={ASSETS.logo}
              alt="Logo Pizzería Estilo Gali"
              className="h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-full bg-[#F5EBD3] p-1.5 border border-[#E6DFD3]"
            />
            <span className="hidden sm:block">
              <span className="block font-serif text-xl sm:text-2xl tracking-tight text-[#2B241D] leading-none">
                Estilo<span className="text-[#D96C42]">.</span>Gali
              </span>
              <span className="block font-sans text-[10px] uppercase tracking-[0.3em] text-[#5C5146] mt-1">
                Pizzería
              </span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testId}
                className="font-sans text-sm text-[#2B241D] hover:text-[#D96C42] transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#D96C42] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-order-btn"
              className="ml-4 inline-flex items-center gap-2 bg-[#2B241D] text-[#F5EBD3] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#D96C42] transition-colors"
            >
              Ordenar ahora
            </a>
            <button
              data-testid="header-cart-btn"
              onClick={openDrawer}
              aria-label="Abrir carrito"
              className="relative inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#D5CBB8] text-[#2B241D] hover:bg-[#EAE3D5] transition-colors"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span
                  data-testid="header-cart-badge"
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#D96C42] text-[#F5EBD3] text-[10px] font-medium flex items-center justify-center"
                >
                  {totalItems}
                </span>
              )}
            </button>
          </nav>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              data-testid="mobile-cart-btn"
              onClick={openDrawer}
              aria-label="Abrir carrito"
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#D5CBB8] text-[#2B241D] hover:bg-[#EAE3D5] transition-colors"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span
                  data-testid="mobile-cart-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D96C42] text-[#F5EBD3] text-[10px] font-medium flex items-center justify-center"
                >
                  {totalItems}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Abrir menú"
              onClick={() => setOpen(!open)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#D5CBB8] text-[#2B241D] hover:bg-[#EAE3D5] transition-colors"
            >
              {open ? (
                <X size={20} strokeWidth={1.5} />
              ) : (
                <MenuIcon size={20} strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#F4EFE6] border-t border-[#E6DFD3]"
        >
          <nav className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                data-testid={`mobile-${l.testId}`}
                className="font-serif text-2xl text-[#2B241D]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              data-testid="mobile-header-order-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#2B241D] text-[#F5EBD3] px-5 py-3 rounded-full text-sm font-medium"
            >
              Ordenar por WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
